export type StudioProject = {
  id: string
  name: string
  cwd: string
  accent: string
  /** Local workspace state. The App Server remains the source of truth for threads and files. */
  pinned?: boolean
  archived?: boolean
  lastOpenedAt?: number
  environment?: 'local' | 'worktree'
  branch?: string
  worktreePath?: string
}

export type TaskMetadata = {
  threadId: string
  projectId: string
  title?: string
  pinned?: boolean
  archived?: boolean
  unread?: boolean
  lastOpenedAt?: number
  environment?: 'local' | 'worktree'
  branch?: string
  cwd?: string
  rawUserText?: string
  internal?: boolean
  internalRole?: string
  parentThreadId?: string
}

export type PermissionProfileId = 'read-only' | 'workspace-auto' | 'granular' | 'full-access'
export type WorkflowReasoningEffort = 'minimal' | 'low' | 'medium' | 'high' | 'xhigh' | 'max' | 'ultra'

const PROJECT_KEY = 'loci.studio.projects.v1'
const ACTIVE_PROJECT_KEY = 'loci.studio.active-project.v1'
const SETTINGS_KEY = 'code.workspace.settings.v1'
const TASK_DRAFTS_KEY = 'code.workspace.task-drafts.v1'
const TASK_QUEUE_KEY = 'code.workspace.task-queue.v1'
const TASK_METADATA_KEY = 'code.workspace.task-metadata.v1'

export type StudioSettings = {
  theme: 'dark' | 'light' | 'system'
  density: 'compact' | 'comfortable'
  memoryContext: boolean
  showSuggestions: boolean
  sendWithEnter: boolean
  reduceMotion: boolean
  defaultModel: string
  /** Default supervisor/worker models used by Plan → build. */
  workflowPlanModel?: string
  workflowBuildModel?: string
  workflowPlanEffort?: WorkflowReasoningEffort
  workflowBuildEffort?: WorkflowReasoningEffort
  permissionProfile: PermissionProfileId
  fullAccessConfirmed: boolean
  approvalReviewer: 'user' | 'auto'
  autoUpdate: boolean
  diagnosticsEnabled: boolean
  logRetention: 'session' | '30-days' | 'forever'
  defaultEnvironment: 'local' | 'worktree'
  defaultBranch: string
  terminalShell: 'system' | 'zsh' | 'bash' | 'powershell'
  notifications: boolean
  remoteEnabled: boolean
  screenReaderAnnouncements: boolean
}

export type DraftAttachment = {
  id: string
  name: string
  mime: string
  size: number
  kind: 'text' | 'image'
  content?: string
  dataUrl?: string
  error?: string
}

export type TaskDraft = {
  text: string
  attachments: DraftAttachment[]
}

export type QueuedDraft = TaskDraft & { id: string; createdAt: number }

function readTaskDrafts(): Record<string, TaskDraft> {
  try {
    const parsed = JSON.parse(localStorage.getItem(TASK_DRAFTS_KEY) ?? '{}') as Record<string, unknown>
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return Object.fromEntries(Object.entries(parsed).filter(([, value]) => {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return false
      const draft = value as Record<string, unknown>
      return typeof draft.text === 'string' && Array.isArray(draft.attachments)
    })) as Record<string, TaskDraft>
  } catch {
    return {}
  }
}

export function loadTaskDraft(key: string): TaskDraft {
  const draft = readTaskDrafts()[key]
  return draft ? { text: draft.text, attachments: draft.attachments.slice(0, 6) } : { text: '', attachments: [] }
}

export function saveTaskDraft(key: string, draft: TaskDraft) {
  const drafts = readTaskDrafts()
  drafts[key] = { text: draft.text.slice(0, 100_000), attachments: draft.attachments.slice(0, 6) }
  const serialized = JSON.stringify(drafts)
  // localStorage is a convenience cache, not the source of truth. Avoid evicting unrelated keys if it is full.
  if (serialized.length <= 4_500_000) localStorage.setItem(TASK_DRAFTS_KEY, serialized)
}

export function clearTaskDraft(key: string) {
  const drafts = readTaskDrafts()
  delete drafts[key]
  localStorage.setItem(TASK_DRAFTS_KEY, JSON.stringify(drafts))
}

export function loadTaskQueue(key: string): QueuedDraft[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(TASK_QUEUE_KEY) ?? '{}') as Record<string, unknown>
    const values = parsed[key]
    if (!Array.isArray(values)) return []
    return values.filter((value): value is QueuedDraft => {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return false
      const item = value as Record<string, unknown>
      return typeof item.id === 'string' && typeof item.text === 'string' && typeof item.createdAt === 'number' && Array.isArray(item.attachments)
    }).slice(0, 50)
  } catch {
    return []
  }
}

export function saveTaskQueue(key: string, queue: QueuedDraft[]) {
  try {
    const parsed = JSON.parse(localStorage.getItem(TASK_QUEUE_KEY) ?? '{}') as Record<string, unknown>
    parsed[key] = queue.slice(0, 50)
    localStorage.setItem(TASK_QUEUE_KEY, JSON.stringify(parsed))
  } catch { /* The queue remains available in the current session. */ }
}

function validTaskMetadata(value: unknown): value is TaskMetadata {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const item = value as Record<string, unknown>
  return typeof item.threadId === 'string' && item.threadId.length > 0
    && typeof item.projectId === 'string' && item.projectId.length > 0
    && (item.title === undefined || typeof item.title === 'string')
    && (item.pinned === undefined || typeof item.pinned === 'boolean')
    && (item.archived === undefined || typeof item.archived === 'boolean')
    && (item.unread === undefined || typeof item.unread === 'boolean')
    && (item.lastOpenedAt === undefined || typeof item.lastOpenedAt === 'number')
    && (item.environment === undefined || item.environment === 'local' || item.environment === 'worktree')
    && (item.branch === undefined || typeof item.branch === 'string')
    && (item.cwd === undefined || typeof item.cwd === 'string')
    && (item.rawUserText === undefined || typeof item.rawUserText === 'string')
    && (item.internal === undefined || typeof item.internal === 'boolean')
    && (item.internalRole === undefined || typeof item.internalRole === 'string')
    && (item.parentThreadId === undefined || typeof item.parentThreadId === 'string')
}

export function loadTaskMetadata(): Record<string, TaskMetadata> {
  try {
    const parsed = JSON.parse(localStorage.getItem(TASK_METADATA_KEY) ?? '{}') as Record<string, unknown>
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return Object.fromEntries(
      Object.entries(parsed)
        .filter(([, value]) => validTaskMetadata(value))
        .sort(([, left], [, right]) => Number((right as TaskMetadata).lastOpenedAt ?? 0) - Number((left as TaskMetadata).lastOpenedAt ?? 0))
        .slice(0, 1000),
    ) as Record<string, TaskMetadata>
  } catch {
    return {}
  }
}

export function saveTaskMetadata(metadata: Record<string, TaskMetadata>) {
  const bounded = Object.fromEntries(Object.entries(metadata).slice(-1000))
  try { localStorage.setItem(TASK_METADATA_KEY, JSON.stringify(bounded)) } catch { /* Metadata is a recoverable local cache. */ }
}

export function updateTaskMetadata(threadId: string, patch: Partial<Omit<TaskMetadata, 'threadId'>>): TaskMetadata {
  const metadata = loadTaskMetadata()
  const current: TaskMetadata = metadata[threadId] ?? { threadId, projectId: 'unknown' }
  const next = { ...current, ...patch, threadId }
  metadata[threadId] = next
  saveTaskMetadata(metadata)
  return next
}

/** Browser-side validation accepts POSIX, Windows drive, and UNC paths. */
export function isAbsoluteWorkspacePath(value: string) {
  return /^(?:[A-Za-z]:[\\/]|\\\\|\/)/.test(value.trim())
}

export const DEFAULT_SETTINGS: StudioSettings = {
  theme: 'dark',
  density: 'compact',
  memoryContext: true,
  showSuggestions: true,
  sendWithEnter: true,
  reduceMotion: false,
  defaultModel: 'auto',
  workflowPlanModel: 'codex:gpt-5.6-sol',
  workflowBuildModel: 'codex:gpt-5.6-luna',
  workflowPlanEffort: 'high',
  workflowBuildEffort: 'low',
  permissionProfile: 'workspace-auto',
  fullAccessConfirmed: false,
  approvalReviewer: 'user',
  autoUpdate: true,
  diagnosticsEnabled: false,
  logRetention: '30-days',
  defaultEnvironment: 'local',
  defaultBranch: 'main',
  terminalShell: 'system',
  notifications: true,
  remoteEnabled: true,
  screenReaderAnnouncements: false,
}

export const DEFAULT_PROJECT: StudioProject = {
  id: 'memory-palace',
  name: 'Code',
  cwd: '/Users/reqsane/Documents/Codex/memory-palace',
  accent: '#8fd6ff',
}

export function loadProjects(): StudioProject[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(PROJECT_KEY) ?? '[]')
    if (!Array.isArray(parsed)) return [DEFAULT_PROJECT]
    const projects = parsed.filter((item): item is StudioProject => (
      item && typeof item.id === 'string' && typeof item.name === 'string' && typeof item.cwd === 'string'
    )).map((item) => item.id === DEFAULT_PROJECT.id && item.name === 'Memory Palace'
      ? { ...item, name: DEFAULT_PROJECT.name }
      : item)
    return projects.length ? projects : [DEFAULT_PROJECT]
  } catch {
    return [DEFAULT_PROJECT]
  }
}

export function saveProjects(projects: StudioProject[]) {
  localStorage.setItem(PROJECT_KEY, JSON.stringify(projects))
}

export function loadActiveProjectId(projects: StudioProject[]) {
  const stored = localStorage.getItem(ACTIVE_PROJECT_KEY)
  return projects.some((project) => project.id === stored) ? stored! : projects[0]!.id
}

export function saveActiveProjectId(projectId: string) {
  localStorage.setItem(ACTIVE_PROJECT_KEY, projectId)
}

export function loadSettings(): StudioSettings {
  try {
    const stored = JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? '{}') as Partial<StudioSettings>
    return {
      theme: stored.theme === 'light' || stored.theme === 'system' ? stored.theme : 'dark',
      density: stored.density === 'comfortable' ? 'comfortable' : 'compact',
      memoryContext: stored.memoryContext !== false,
      showSuggestions: stored.showSuggestions !== false,
      sendWithEnter: stored.sendWithEnter !== false,
      reduceMotion: stored.reduceMotion === true,
      defaultModel: typeof stored.defaultModel === 'string' && stored.defaultModel ? stored.defaultModel : 'auto',
      workflowPlanModel: typeof stored.workflowPlanModel === 'string' && stored.workflowPlanModel ? stored.workflowPlanModel : DEFAULT_SETTINGS.workflowPlanModel,
      workflowBuildModel: typeof stored.workflowBuildModel === 'string' && stored.workflowBuildModel ? stored.workflowBuildModel : DEFAULT_SETTINGS.workflowBuildModel,
      workflowPlanEffort: stored.workflowPlanEffort === 'minimal' || stored.workflowPlanEffort === 'low' || stored.workflowPlanEffort === 'medium' || stored.workflowPlanEffort === 'high' || stored.workflowPlanEffort === 'xhigh' || stored.workflowPlanEffort === 'max' || stored.workflowPlanEffort === 'ultra'
        ? stored.workflowPlanEffort
        : DEFAULT_SETTINGS.workflowPlanEffort,
      workflowBuildEffort: stored.workflowBuildEffort === 'minimal' || stored.workflowBuildEffort === 'low' || stored.workflowBuildEffort === 'medium' || stored.workflowBuildEffort === 'high' || stored.workflowBuildEffort === 'xhigh' || stored.workflowBuildEffort === 'max' || stored.workflowBuildEffort === 'ultra'
        ? stored.workflowBuildEffort
        : DEFAULT_SETTINGS.workflowBuildEffort,
      permissionProfile: stored.permissionProfile === 'read-only' || stored.permissionProfile === 'granular' || stored.permissionProfile === 'full-access' || stored.permissionProfile === 'workspace-auto'
        ? stored.permissionProfile
        : DEFAULT_SETTINGS.permissionProfile,
      fullAccessConfirmed: stored.fullAccessConfirmed === true && stored.permissionProfile === 'full-access',
      approvalReviewer: stored.approvalReviewer === 'auto' ? 'auto' : 'user',
      autoUpdate: stored.autoUpdate !== false,
      diagnosticsEnabled: stored.diagnosticsEnabled === true,
      logRetention: stored.logRetention === 'session' || stored.logRetention === 'forever' ? stored.logRetention : '30-days',
      defaultEnvironment: stored.defaultEnvironment === 'worktree' ? 'worktree' : 'local',
      defaultBranch: typeof stored.defaultBranch === 'string' && /^[A-Za-z0-9._/-]{1,120}$/.test(stored.defaultBranch) ? stored.defaultBranch : 'main',
      terminalShell: stored.terminalShell === 'zsh' || stored.terminalShell === 'bash' || stored.terminalShell === 'powershell' ? stored.terminalShell : 'system',
      notifications: stored.notifications !== false,
      remoteEnabled: stored.remoteEnabled !== false,
      screenReaderAnnouncements: stored.screenReaderAnnouncements === true,
    }
  } catch {
    return DEFAULT_SETTINGS
  }
}

export function saveSettings(settings: StudioSettings) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
}
